import m from '@bitstillery/mithril'
import {AuthLogin as AuthLoginCommon} from '@bitstillery/common/components/portal'
import {set_language} from '@bitstillery/common/lib/i18n'
import {$t, api, notifier} from '@bitstillery/common/app'
import type {ErrorResponse, PortalLoginResponse, VerifyOTPRequest} from '@bitstillery/common/api/fact2server'
import {AccountSlug, LoginErrorMessage} from '@bitstillery/common/api/fact2server'
import {is_one_of, plausible_event, route_param_text} from '@bitstillery/common/lib/utils'
import {PlausibleEvent} from '@bitstillery/common/types/utils'

import {$m, $s} from '@/app'
import {toggle_tawk} from '@/lib/tawk'
import {DEFAULT_ROUTE} from '@/routes'

/**
 * ?redirect comes from the URL, and this navigates with window.location, which follows a full URL: without the
 * check, a crafted login link could send a user to another site right after they sign in. "//x" and "/\x"
 * are protocol-relative to browsers, so they don't count as a path.
 */
function is_same_site_path(target: string): boolean {
    return target.startsWith('/') && !target.startsWith('//') && !target.startsWith('/\\')
}

export class AuthLogin extends AuthLoginCommon {
    override redirect_to = route_param_text(m.route.param('redirect'))

    override oncreate() {
        toggle_tawk(false)
    }

    override onupdate() {
        toggle_tawk(false)
    }

    async login() {
        // The portal is built per account, with that account's slug as its theme.
        const account_slug = process.env.MSI_THEME
        if (!is_one_of(Object.values(AccountSlug), account_slug)) {
            return this._handle_login_fail()
        }
        const data = {
            account_slug,
            password: this.data.form.password,
            username: this.data.form.email_address,
            username_support: this.data.form.email_support,
        }
        const {success, result, error_result} = await api.post('portal/login', data, true)

        if (!success) {
            return this._handle_login_fail(error_result)
        }

        if (result.otp_required) {
            this._handle_otp_required(result)
        } else if (result.token) {
            await this._handle_login_success(result.token)
        } else {
            notifier.notify($t('status.error.unknown'), 'danger')
        }
    }

    async verify_otp() {
        if (!this.data.pre_auth_token) {
            return notifier.notify($t('status.error.unknown'), 'danger')
        }

        const data: VerifyOTPRequest = {otp: this.data.form.otp_code, pre_auth_token: this.data.pre_auth_token}

        const {success, result, error_result} = await api.post('portal/verify-otp', data, true)

        if (!success) {
            return this._handle_login_fail(error_result)
        }
        if (result.token) {
            await this._handle_login_success(result.token)
        } else {
            notifier.notify($t('status.error.unknown'), 'danger')
        }
    }

    _handle_login_fail(error_result?: ErrorResponse): void {
        const message = error_result?.detail

        let notify = $t('status.error.unknown')

        if (message === LoginErrorMessage.CREDENTIALS_INVALID || message === LoginErrorMessage.CREDENTIALS_INVALID_SUPPORT) {
            notify = $t('status.error.login.credentials_invalid')
        } else if (message === LoginErrorMessage.ACCOUNT_DISABLED) {
            notify = $t('status.error.login.account_disabled')
        } else if (message === LoginErrorMessage.ACCOUNT_DISABLED_SUPPORT) {
            notify = $t('status.error.login.account_disabled_support')
        } else if (
            message === LoginErrorMessage.TOKEN_EXPIRED ||
            message === LoginErrorMessage.TOKEN_MALFORMED ||
            message === LoginErrorMessage.OTP_NOT_SET_UP
        ) {
            notify = $t('status.error.login.pre_auth_token_expired')
            this.data.stepper.selection = 0
        } else if (message === LoginErrorMessage.OTP_INVALID) {
            this.data.otp_fail = message
            notify = $t('status.error.otp.invalid_notify')
        }

        notifier.notify(notify, 'danger', 7000)
        plausible_event(PlausibleEvent.ACCESS, {props: {type: 'access_login_fail'}})
    }

    _handle_otp_required(result: PortalLoginResponse): void {
        if (!result.token) {
            notifier.notify($t('status.error.unknown'), 'danger')
            return
        }

        this.data.pre_auth_token = result.token
        if (result.otp_secret_url) {
            // First time setup OTP
            this.data.otp_secret = result.otp_secret_url
            this.data.stepper.selection = 1
            plausible_event(PlausibleEvent.ACCESS, {props: {type: 'access_login_otp'}})
        } else {
            this.data.stepper.selection = 2
        }
    }

    async _handle_login_success(token: string) {
        await $m.identity.new_token(token)

        void set_language($s.identity.user.language)
        if (this.redirect_to && is_same_site_path(this.redirect_to)) {
            window.location.href = this.redirect_to
        } else {
            void m.route.set(DEFAULT_ROUTE)
        }
        notifier.notify($t('auth.login_success', {vendor: process.env.MSI_TITLE}), 'success')
        plausible_event(PlausibleEvent.ACCESS, {props: {type: 'access_login'}})
    }
}
